/**
 * Data of the line tabs: the line's machines in flow order, and one machine
 * statement asked for each of them.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import { ChartDataService, type ChartRequestParams } from "../../services/ChartDataService";
import { getLineKPIConfig } from "../../services/LineConfigService";
import type { NATSResponse } from "../../services/OrgNATSClient";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { lineStations, type LineStation } from "./lineStations";

const TTL_MS = 5 * 60 * 1000;
/** Requests in flight at once, so a long line does not flood the data engine. */
const CONCURRENCY = 6;
/** More machines than this are not asked (the few very long "lines" are plant buckets). */
export const MAX_LINE_MACHINES = 40;

// ------------------------------------------------------------ stations

/** What the line tabs use of a line's KPI configuration. */
export interface LineConfigInfo {
  /** The machine (sn) the line output is counted at; unset: the last flow group. */
  outputMachineId?: string;
  /** "Contains" per machine (sn): how many of the previous unit fit in its unit. */
  contains: Record<string, number>;
}

const NO_CONFIG: LineConfigInfo = { contains: {} };
const configCache = new Map<string, { at: number; value: LineConfigInfo }>();
const configLoads = new Map<string, Promise<LineConfigInfo>>();
const configListeners = new Set<(lineId: string) => void>();

/** Reload a line's configuration, e.g. after its KPI configuration was saved. */
export function invalidateLineStations(lineId: string) {
  configCache.delete(lineId);
  configListeners.forEach((listener) => listener(lineId));
}

/** One request per line at a time, whoever asks. */
function loadConfig(lineId: string, token: string): Promise<LineConfigInfo> {
  let load = configLoads.get(lineId);
  if (!load) {
    load = getLineKPIConfig(lineId, token)
      .then((config) => {
        const value: LineConfigInfo = {
          outputMachineId: config?.outputMachineId || undefined,
          contains: Object.fromEntries(
            (config?.machines ?? [])
              .filter((m) => (m.unitConversion?.unitsPerContainer ?? 0) > 0)
              .map((m) => [m.machineId, m.unitConversion!.unitsPerContainer]),
          ),
        };
        configCache.set(lineId, { at: Date.now(), value });
        return value;
      })
      .finally(() => configLoads.delete(lineId));
    configLoads.set(lineId, load);
  }
  return load;
}

/**
 * The output machine and the "Contains" values of a line's KPI configuration,
 * loaded once per line and kept 5 minutes. Without a configuration the
 * default rule applies (the highest flow group, factor 1).
 */
export function useLineConfigInfo(lineId: string | undefined): LineConfigInfo {
  const { token } = useUnifiedAuth();
  const [info, setInfo] = useState<LineConfigInfo>(
    () => (lineId ? configCache.get(lineId)?.value : undefined) ?? NO_CONFIG,
  );
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const listener = (id: string) => {
      if (id === lineId) setReload((n) => n + 1);
    };
    configListeners.add(listener);
    return () => {
      configListeners.delete(listener);
    };
  }, [lineId]);

  useEffect(() => {
    if (!lineId || !token) return;
    const cached = configCache.get(lineId);
    if (cached && Date.now() - cached.at < TTL_MS) {
      setInfo(cached.value);
      return;
    }
    let cancelled = false;
    loadConfig(lineId, token)
      .then((value) => {
        if (!cancelled) setInfo(value);
      })
      .catch(() => {
        // no configuration: the default rule applies
      });
    return () => {
      cancelled = true;
    };
  }, [lineId, token, reload]);

  return info;
}

/**
 * The machines of a line in flow order, with the output machine of its KPI
 * configuration: those with data, or all of them (`includeWithoutData`, e.g.
 * for live data, which can come before the first shift reaches the cockpit).
 */
export function useLineStations(
  lineId: string | undefined,
  options?: { includeWithoutData?: boolean },
): LineStation[] {
  const machines = useSAPDataStore((s) => s.machines);
  const { outputMachineId } = useLineConfigInfo(lineId);
  const includeAll = options?.includeWithoutData === true;

  return useMemo(() => {
    if (!lineId) return [];
    const ofLine = machines.filter((m) => m.lineId === lineId && (includeAll || m.datenVerfuegbar));
    return lineStations(lineId, ofLine, outputMachineId);
  }, [lineId, machines, outputMachineId, includeAll]);
}

// ------------------------------------------------------------- fan-out

type Fetcher = (params: ChartRequestParams, token: string) => Promise<NATSResponse>;

export interface FanoutState {
  /** The statement's payload per machine (sn). */
  data: Map<string, unknown>;
  /** Machines whose request failed, with the reason (e.g. no access). */
  errors: Map<string, string>;
  loading: boolean;
}

const fanoutCache = new Map<string, { at: number; value: unknown }>();

/**
 * Asks one machine statement for every machine (`sns`), with the global date
 * range and the selected shifts, a few requests at a time. Answers are kept
 * 5 minutes per machine, range and shifts, so switching tabs does not ask
 * again.
 */
export function useLineFanout(
  kind: string,
  sns: string[],
  fetcher: Fetcher,
  shifts: string[],
  extraParams?: Record<string, unknown>,
): FanoutState {
  const { token } = useUnifiedAuth();
  const start = useSAPDataStore((s) => s.dateRange.start);
  const end = useSAPDataStore((s) => s.dateRange.end);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const extraRef = useRef(extraParams);
  extraRef.current = extraParams;

  const [state, setState] = useState<FanoutState>({
    data: new Map(),
    errors: new Map(),
    loading: true,
  });

  const snsKey = sns.join(",");
  const shiftsKey = [...shifts].sort().join(",");
  const extraKey = JSON.stringify(extraParams ?? {});

  useEffect(() => {
    const list = snsKey ? snsKey.split(",") : [];
    const shiftList = shiftsKey ? shiftsKey.split(",") : [];
    if (!token || !start || !end || list.length === 0) {
      setState({ data: new Map(), errors: new Map(), loading: false });
      return;
    }

    const keyOf = (sn: string) => [kind, sn, start, end, shiftsKey, extraKey].join("|");
    const data = new Map<string, unknown>();
    const errors = new Map<string, string>();
    const todo: string[] = [];
    for (const sn of list) {
      const hit = fanoutCache.get(keyOf(sn));
      if (hit && Date.now() - hit.at < TTL_MS) data.set(sn, hit.value);
      else todo.push(sn);
    }
    setState({ data: new Map(data), errors, loading: todo.length > 0 });
    if (todo.length === 0) return;

    let cancelled = false;
    let next = 0;
    const worker = async () => {
      while (!cancelled && next < todo.length) {
        const sn = todo[next++];
        try {
          const params = ChartDataService.buildRequestParams(
            sn,
            { start, end },
            shiftList,
            undefined,
            extraRef.current,
          );
          const res = await fetcherRef.current(params, token);
          if (res.success) {
            fanoutCache.set(keyOf(sn), { at: Date.now(), value: res.data ?? null });
            data.set(sn, res.data ?? null);
          } else {
            errors.set(sn, res.error || "request failed");
          }
        } catch (e) {
          errors.set(sn, e instanceof Error ? e.message : String(e));
        }
      }
    };
    void Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker),
    ).then(() => {
      if (!cancelled) setState({ data: new Map(data), errors: new Map(errors), loading: false });
    });
    return () => {
      cancelled = true;
    };
  }, [kind, snsKey, shiftsKey, extraKey, token, start, end]);

  return state;
}
